import { Box, type SxProps, type Theme } from "@mui/material";
import type { FC } from "react";
import { NEUTRAL } from "../../../theme/tokens";

export type ColorDotSize = "xs" | "sm" | "md";

const SIZE_PX: Record<ColorDotSize, number> = { xs: 6, sm: 8, md: 10 };

/**
 * The single categorical marker: a filled circle for "belongs to that colour's bucket". Replaces the
 * hand-rolled `borderRadius: "50%"` Box each surface used to write, which drifted in diameter so a
 * band's dot and its list's dots didn't match.
 *
 * `aria-hidden` by default — the colour sits beside the label it encodes; pass `ariaLabel` only when
 * the dot is the sole statement of its category. Colour must be a resolved theme token, never a
 * call-site hex; with none it falls back to neutral so a missing category reads as unclassified.
 */
export const ColorDot: FC<{
	/** Resolved colour token. Omitted → a muted neutral, i.e. "no category". */
	color?: string;
	/** `xs` 6px · `sm` 8px (default) · `md` 10px. */
	size?: ColorDotSize;
	/** Makes the dot meaningful to assistive tech. Omit when an adjacent label already names the category. */
	ariaLabel?: string;
	sx?: SxProps<Theme>;
	dataTestId?: string;
}> = ({ color, size = "sm", ariaLabel, sx, dataTestId }) => (
	<Box
		component="span"
		aria-hidden={ariaLabel ? undefined : true}
		aria-label={ariaLabel}
		role={ariaLabel ? "img" : undefined}
		data-testid={dataTestId}
		sx={[
			{
				flexShrink: 0,
				display: "inline-block",
				width: SIZE_PX[size],
				height: SIZE_PX[size],
				borderRadius: "50%",
				backgroundColor: color ?? NEUTRAL[400],
			},
			...(Array.isArray(sx) ? sx : [sx]),
		]}
	/>
);

export default ColorDot;
